/* global process */
import React from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';
import questionHelperService from '../../services/questionView/QuestionHelperServices';
import presignUrlService from '../../services/PresignUrlService';
import PDFJSLoader from '../../loading/resources/libraries/PDFJS';
import PDFJSViewerImages from '../../loading/resources/libraries/PDFJSViewerImages';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import utilService from '../../services/UtilService';

import styles from './QuestionPDFPanelDisplay.scss';

class QuestionPDFPanelDisplay extends React.Component {
    static propTypes = {
        model: PropTypes.object.isRequired,
        onReady: PropTypes.func.isRequired,
        presignedUrlsForPDFPanel: PropTypes.object,
        questionContentRevisionId: PropTypes.number.isRequired,
        stimulusPosition: PropTypes.string,
    };

    state = {
        position: {
            x: 0, y: 0,
        },
        resizeAxis: 'v',
        pdfPanelHeight: 500,
        // Blob URL built from the cached PDF and pdf.js assets. Used as the primary
        // offline viewer for all scenarios — both SW-controlled and non-SW pages.
        offlinePdfBlobUrl: null,
    };

    componentDidMount() {
        this.showPanel();
        this.calculateInteraction();
        if (marketplacePropertiesService.isOfflineResilienceForPdfViewerEnabled()) {
            if (!this.isOffline()) {
                PDFJSLoader.cacheViewerFiles();
            }
            // Always load blob URL eagerly regardless of online state.
            // navigator.onLine is unreliable when the device has a local network
            // connection but no actual internet (e.g. WiFi up, router has no internet).
            // In that scenario the offline event never fires, so waiting for it would
            // leave the PDF blank. Loading proactively ensures the blob is ready as
            // the primary no-network source the moment the user needs it.
            this.loadOfflinePdfBlobUrl();
            window.addEventListener('offline', this.handleOfflineEvent);
            window.addEventListener('online', this.handleOnlineEvent);
        }
        const qtiElement = Array.from(this.pdfPanel.parentElement.children).find(child =>
            child.id.includes(`question-${this.props.questionContentRevisionId}`)
        );
        setTimeout(() => {
            if (qtiElement.offsetHeight > this.state.pdfPanelHeight) {
                this.setState({ pdfPanelHeight: qtiElement.offsetHeight });
            }
        }, 100);
        this.refreshPresignedUrlIfNeeded();
    }

    componentDidUpdate(prevProps) {
        this.refreshPresignedUrlIfNeeded();
        if (!marketplacePropertiesService.isOfflineResilienceForPdfViewerEnabled()) {
            return;
        }
        if (prevProps.model.pdfSourceUrl !== this.props.model.pdfSourceUrl) {
            if (this.offlineBlobUrls && this.offlineBlobUrls.length) {
                // Stage old blob URLs for deferred revocation instead of revoking them
                // immediately. They will be revoked in loadOfflinePdfBlobUrl just before
                // the new blob URL is committed to state, so the iframe continues showing
                // the previous question's PDF during the async cache read rather than
                // flashing a 404/error screen when the network is physically disconnected.
                this.pendingRevokeUrls = [
                    ...(this.pendingRevokeUrls || []),
                    ...this.offlineBlobUrls,
                ];
                this.offlineBlobUrls = [];
            }
            // loadOfflinePdfBlobUrl's guard now uses loadedPdfSourceUrl to distinguish
            // "same question already loaded" from "new question needs loading", so there
            // is no need to clear offlinePdfBlobUrl from state before calling it.
            this.loadOfflinePdfBlobUrl();
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        window.removeEventListener('offline', this.handleOfflineEvent);
        window.removeEventListener('online', this.handleOnlineEvent);
        if (this.offlineBlobUrls) {
            this.offlineBlobUrls.forEach(url => URL.revokeObjectURL(url));
        }
        if (this.pendingRevokeUrls) {
            this.pendingRevokeUrls.forEach(url => URL.revokeObjectURL(url));
        }
    }

    isOffline = () => {
        return window.offlineMode || !utilService.isBrowserStatusOnline();
    };

    isSwControllingPage = () => {
        return !!(navigator.serviceWorker && navigator.serviceWorker.controller);
    };

    handleOfflineEvent = () => {
        this.loadOfflinePdfBlobUrl();
    };

    handleOnlineEvent = () => {
        // Do NOT revoke blob URLs or clear offlinePdfBlobUrl immediately.
        // The iframe keeps showing the cached PDF via blob URL as a seamless bridge
        // while the presigned URL is fetched below. componentDidUpdate cleans up
        // once a valid presigned URL is confirmed ready.
        PDFJSLoader.cacheViewerFiles();
        // Start the presigned URL refresh now that we are back online.
        // Without this explicit call nothing triggers refreshPresignedUrlIfNeeded until
        // the next props/state change, so the deferred cleanup in componentDidUpdate
        // would never fire and the blob URL would remain set indefinitely.
        this.refreshPresignedUrlIfNeeded();
    };

    /**
     * Reads the cached PDF and pdf.js library directly via the Cache API, builds an
     * inline pdf.js-powered viewer as a blob URL, and sets it in state so the iframe
     * can display the PDF without any network access or SW interception.
     * Used for all offline scenarios, including when the SW is controlling the page,
     * because the SW path (serving viewer.html as opaque + re-fetching the PDF) is
     * unreliable across environments.
     */
    loadOfflinePdfBlobUrl = async () => {
        const { model } = this.props;
        // Capture the URL at call time so we can detect a question change after each await.
        const { pdfSourceUrl } = model;
        // Skip only when we already have a blob URL for THIS exact question.
        // Using loadedPdfSourceUrl (not just checking offlinePdfBlobUrl) ensures we
        // reload when the question changes while the old blob URL is still in state
        // (deferred-revoke pattern that prevents 404 flash during question navigation).
        if (this.state.offlinePdfBlobUrl && this.loadedPdfSourceUrl === pdfSourceUrl) {
            return;
        }
        if (!pdfSourceUrl || !('caches' in window)) {
            return;
        }
        try {
            const [pdfResponse, pdfJsResponse, pdfWorkerResponse] = await Promise.all([
                caches.match(pdfSourceUrl, { ignoreSearch: true }),
                caches.match(PDFJSLoader.getPdfJsCachedUrl(), { ignoreSearch: true }),
                caches.match(PDFJSLoader.getPdfWorkerCachedUrl(), { ignoreSearch: true }),
            ]);

            if (!pdfResponse || !pdfResponse.ok) {
                return;
            }

            const pdfArrayBuffer = await pdfResponse.arrayBuffer();
            if (pdfArrayBuffer.byteLength === 0) {
                return;
            }
            // Guard before creating any blob URLs: bail out if the component unmounted or
            // the user navigated to a different question while the cache reads were in flight.
            if (this.unmounted || this.props.model.pdfSourceUrl !== pdfSourceUrl) {
                return;
            }
            const pdfBlobUrl = URL.createObjectURL(new Blob([pdfArrayBuffer], { type: 'application/pdf' }));
            this.offlineBlobUrls = [pdfBlobUrl];

            let blobUrlToSet;
            if (pdfJsResponse && pdfJsResponse.ok && pdfWorkerResponse && pdfWorkerResponse.ok) {
                blobUrlToSet = await this.buildPdfJsViewerBlobUrl(
                    pdfArrayBuffer, pdfBlobUrl, pdfJsResponse, pdfWorkerResponse
                );
            } else {
                // pdf.js not available in cache (opaque or missing) — fall back to raw PDF.
                blobUrlToSet = pdfBlobUrl;
            }

            // Final guard: verify the question still matches and component is still mounted
            // before committing to state. If either changed (rapid navigation or unmount),
            // revoke all blob URLs created above to prevent leaks, then discard.
            if (this.unmounted || this.props.model.pdfSourceUrl !== pdfSourceUrl) {
                this.offlineBlobUrls.forEach(url => URL.revokeObjectURL(url));
                this.offlineBlobUrls = [];
                return;
            }
            // Now that the new blob is ready, revoke the previous question's blob URLs
            // that were staged in componentDidUpdate. Revocation is deferred to this
            // point (rather than clearing them immediately on question change) so the
            // iframe keeps displaying the old PDF during the async cache read —
            // preventing the brief 404/error flash when physically offline.
            if (this.pendingRevokeUrls) {
                this.pendingRevokeUrls.forEach(url => URL.revokeObjectURL(url));
                this.pendingRevokeUrls = null;
            }
            this.loadedPdfSourceUrl = pdfSourceUrl;
            this.setState({ offlinePdfBlobUrl: blobUrlToSet });
        } catch (err) {
            // Silently ignore — offline PDF viewer is best-effort
        }
    };

    /**
     * Reads pdf.js / pdf.worker from cache, resolves viewer assets, and returns the
     * blob URL of an iframe-ready HTML viewer.  Extracted from loadOfflinePdfBlobUrl
     * to keep that method's cognitive complexity within the allowed limit.
     */
    buildPdfJsViewerBlobUrl = async (pdfArrayBuffer, pdfBlobUrl, pdfJsResponse, pdfWorkerResponse) => {
        const [pdfJsText, pdfWorkerText] = await Promise.all([
            pdfJsResponse.text(),
            pdfWorkerResponse.text(),
        ]);
        const pdfJsBlobUrl = URL.createObjectURL(new Blob([pdfJsText], { type: 'application/javascript' }));
        const pdfWorkerBlobUrl = URL.createObjectURL(new Blob([pdfWorkerText], { type: 'application/javascript' }));
        this.offlineBlobUrls.push(pdfJsBlobUrl, pdfWorkerBlobUrl);

        // Prefer the real PDFJS viewer when viewer.html was cached as a readable
        // (CORS-accessible) response — happens in production where the CDN allows
        // the player origin. Falls back to the custom viewer in dev (CORS blocked).
        const [viewerHtmlResponse, viewerJsResponse, viewerCssResponse] = await Promise.all([
            caches.match(PDFJSLoader.getViewerHtmlReadableKey()),
            caches.match(PDFJSLoader.getViewerJsCachedUrl()),
            caches.match(PDFJSLoader.getViewerCssCachedUrl()),
        ]);

        let viewerBlobUrl;
        if (viewerHtmlResponse && viewerHtmlResponse.ok
            && viewerJsResponse && viewerJsResponse.ok
            && viewerCssResponse && viewerCssResponse.ok) {
            // Full PDFJS viewer: rewrite viewer.html's relative script/style references
            // to blob URLs so the iframe can load everything without network access.
            const [viewerJsText, viewerCssText, viewerHtmlText] = await Promise.all([
                viewerJsResponse.text(),
                viewerCssResponse.text(),
                viewerHtmlResponse.text(),
            ]);

            // Encode the PDF as base64 and embed it in the injected script.
            // This avoids any URL fetch from inside the iframe — PDFJS receives
            // the raw bytes directly via open({data: Uint8Array}), which is more
            // reliable than fetching a blob URL created in the parent context.
            let pdfBinary = '';
            const pdfBytes = new Uint8Array(pdfArrayBuffer);
            for (let i = 0; i < pdfBytes.length; i += 8192) {
                pdfBinary += String.fromCharCode(...pdfBytes.subarray(i, i + 8192));
            }
            const pdfBase64 = btoa(pdfBinary);
            const pdfWorkerBlobUrlJson = JSON.stringify(pdfWorkerBlobUrl);

            const viewerJsBlobUrl = URL.createObjectURL(new Blob([viewerJsText], { type: 'application/javascript' }));

            // Rewrite relative url() references (images) in viewer.css so they
            // resolve from a blob: context. Priority order:
            //   1. Embedded base64 data URI (always works, no network/cache needed)
            //   2. Cache Storage blob URL (works offline if previously cached)
            //   3. Absolute CDN URL (fallback, requires network)
            const viewerWebCachedRoot = PDFJSLoader.getViewerWebCachedRoot();
            const viewerWebCdnRoot = PDFJSLoader.getViewerWebCdnRoot();
            // Matches relative url() references in CSS, excluding data:, blob:, http(s):, and absolute paths.
            const relativeCssUrlRegex = /url\(['"]?((?!data:|blob:|https?:|\/\/)([^'")#?\s]+))['"]?\)/gi;
            const relPathSet = new Set();
            viewerCssText.replace(
                relativeCssUrlRegex,
                (_, relPath) => relPathSet.add(relPath)
            );
            const cssUrlMap = Object.create(null);
            await Promise.all([...relPathSet].map(async (relPath) => {
                // 1. Embedded base64 — most reliable, no I/O needed
                if (PDFJSViewerImages[relPath]) {
                    cssUrlMap[relPath] = PDFJSViewerImages[relPath];
                    return;
                }
                // 2. Cache Storage
                try {
                    const cached = await caches.match(`${viewerWebCachedRoot}${relPath}`, { ignoreSearch: true });
                    if (cached && cached.ok) {
                        const blobUrl = URL.createObjectURL(await cached.blob());
                        this.offlineBlobUrls.push(blobUrl);
                        cssUrlMap[relPath] = blobUrl;
                        return;
                    }
                } catch (e) { /* ignore */ }
                // 3. CDN URL
                cssUrlMap[relPath] = `${viewerWebCdnRoot}${relPath}`;
            }));
            const rewrittenCss = viewerCssText.replace(
                relativeCssUrlRegex,
                (_, relPath) => `url(${cssUrlMap[relPath]})`
            );
            const viewerCssBlobUrl = URL.createObjectURL(new Blob([rewrittenCss], { type: 'text/css' }));
            this.offlineBlobUrls.push(viewerJsBlobUrl, viewerCssBlobUrl);

            // Injected BEFORE viewer.js so it runs first.
            //
            // Goal: patch PDFViewerApplication before initialize() is called so that:
            //   - _initializeWorker uses our worker blob URL (not the default relative path)
            //   - defaultUrl is '' so no auto-open is attempted with an invalid URL
            //   - createL10n returns a no-op so XHRs to locale files never happen
            //
            // Strategy (belt-and-suspenders):
            //   A) webviewerloaded listener on both document and window
            //   B) setInterval(1ms) polling for PDFViewerApplication to exist
            //   C) _initializeWorker method patch so the worker URL is injected
            //      right before the worker is actually created (most reliable)
            //   D) Pre-set window.PDFViewerApplicationOptions for PDFJS builds
            //      that read it at module-init time
            //   E) XHR open() patch for any remaining relative-URL XHRs
            /* eslint-disable max-len */
            const appOptionsScript = `<script>(function(){
var workerBlobUrl=${pdfWorkerBlobUrlJson};
var noopL10n={getDirection:function(){return Promise.resolve('ltr');},getLanguage:function(){return Promise.resolve('en-US');},get:function(k,a,f){return Promise.resolve(f||k);},translate:function(){return Promise.resolve();},destroy:function(){return Promise.resolve();}};

function patchApp(){
  var app=window.PDFViewerApplication;
  if(!app)return false;
  if(app.externalServices&&!app._l10nPatched){
    app._l10nPatched=true;
    app.externalServices.createL10n=function(){return noopL10n;};
  }
  if(typeof app._initializeWorker==='function'&&!app._workerPatched){
    app._workerPatched=true;
    var origIW=app._initializeWorker.bind(app);
    app._initializeWorker=function(){
      if(window.pdfjsLib)pdfjsLib.GlobalWorkerOptions.workerSrc=workerBlobUrl;
      if(window.PDFViewerApplicationOptions&&typeof PDFViewerApplicationOptions.set==='function'){
        try{PDFViewerApplicationOptions.set('workerSrc',workerBlobUrl);}catch(x){}
      }
      return origIW();
    };
  }
  if(window.PDFViewerApplicationOptions){
    try{
      if(typeof PDFViewerApplicationOptions.set==='function'){
        PDFViewerApplicationOptions.set('defaultUrl','');
        PDFViewerApplicationOptions.set('workerSrc',workerBlobUrl);
      }else{
        PDFViewerApplicationOptions.defaultUrl='';
        PDFViewerApplicationOptions.workerSrc=workerBlobUrl;
      }
    }catch(e){}
  }
  if(window.pdfjsLib){pdfjsLib.GlobalWorkerOptions.workerSrc=workerBlobUrl;}
  return true;
}

function onViewerLoaded(){patchApp();}
document.addEventListener('webviewerloaded',onViewerLoaded,{once:true});
window.addEventListener('webviewerloaded',onViewerLoaded,{once:true});

var _pi=setInterval(function(){
  if(window.PDFViewerApplication){clearInterval(_pi);patchApp();}
},1);

var oO=XMLHttpRequest.prototype.open,oS=XMLHttpRequest.prototype.send;
XMLHttpRequest.prototype.open=function(m,u,a,b,c){
  try{return oO.apply(this,arguments);}
  catch(e){
    if(e.name==='SyntaxError'){
      this._s=true;
      try{Object.defineProperty(this,'readyState',{value:4,configurable:true,writable:true});}catch(x){}
      try{Object.defineProperty(this,'status',{value:200,configurable:true,writable:true});}catch(x){}
      try{Object.defineProperty(this,'responseText',{value:'',configurable:true,writable:true});}catch(x){}
      try{Object.defineProperty(this,'response',{value:'',configurable:true,writable:true});}catch(x){}
    }else{throw e;}
  }
};
XMLHttpRequest.prototype.send=function(b){
  if(this._s){var t=this;setTimeout(function(){try{if(t.onreadystatechange)t.onreadystatechange.call(t,{});}catch(e){}try{if(t.onload)t.onload.call(t,{});}catch(e){}},0);return;}
  return oS.apply(this,arguments);
};
try{window.PDFViewerApplicationOptions=Object.assign(window.PDFViewerApplicationOptions||{},{workerSrc:workerBlobUrl,defaultUrl:''});}catch(e){}
})();</script>`;
            /* eslint-enable max-len */

            // Injected after viewer.js (before </body>):
            // pdfOpenFallback embeds the PDF as base64 and passes raw bytes to
            // PDFViewerApplication.open({data:...}), avoiding any URL-fetch entirely.
            // Uses polling because webviewerloaded has already fired by the time this
            // script runs (viewer.js precedes it in the DOM).
            /* eslint-disable max-len */
            const pdfOpenFallback = `<script>(function(){
var b64=${JSON.stringify(pdfBase64)},w=${pdfWorkerBlobUrlJson};
function b64ToArr(s){var b=atob(s),a=new Uint8Array(b.length);for(var i=0;i<b.length;i++)a[i]=b.charCodeAt(i);return a;}
var pdfData=b64ToArr(b64);
var opened=false;
function tryOpen(n){
  var app=window.PDFViewerApplication;
  if(app&&app.initialized){
    if(!opened){
      opened=true;
      if(window.pdfjsLib)pdfjsLib.GlobalWorkerOptions.workerSrc=w;
      try{app.open(pdfData);}catch(e){}
    }
  }else if(n>0){
    setTimeout(function(){tryOpen(n-1);},200);
  }
}
tryOpen(40);
})();</script>`;
            /* eslint-enable max-len */

            // Belt-and-suspenders: also set workerSrc immediately after pdf.js loads.
            const workerOverride = `<script>window.pdfjsLib&&(pdfjsLib.GlobalWorkerOptions.workerSrc=${pdfWorkerBlobUrlJson});</script>`;
            const rewritten = viewerHtmlText
                // Replace the entire <script src="...pdf.js"></script> tag and append worker override.
                .replace(
                    /(<script\s[^>]*src="[^"]*pdf\.js"[^>]*><\/script>)/gi,
                    `<script src="${pdfJsBlobUrl}"></script>${workerOverride}`
                )
                // Inject PDFViewerApplicationOptions + replace viewer.js script src.
                // appOptionsScript must run BEFORE viewer.js so AppOptions picks it up.
                .replace(/(<script\s[^>]*src=")viewer\.js(")/gi, `${appOptionsScript}<script src="${viewerJsBlobUrl}"`)
                // Replace viewer.css href.
                .replace(/(<link\s[^>]*href=")viewer\.css(")/gi, `$1${viewerCssBlobUrl}$2`)
                // Inject fallback open() call before </body> (case-insensitive).
                .replace(/<\/body>/i, `${pdfOpenFallback}</body>`);

            const viewerHtmlBlobUrl = URL.createObjectURL(new Blob([rewritten], { type: 'text/html' }));
            this.offlineBlobUrls.push(viewerHtmlBlobUrl);

            // Use plain blob URL — PDF URL is passed via PDFViewerApplicationOptions.
            // Blob URLs do not support query parameters so ?file= cannot be used.
            viewerBlobUrl = viewerHtmlBlobUrl;
        } else {
            // viewer.html not readable (opaque in cache, dev/localhost) — use custom viewer.
            const viewerHtml = this.buildOfflinePdfViewerHtml(pdfBlobUrl, pdfJsBlobUrl, pdfWorkerBlobUrl);
            viewerBlobUrl = URL.createObjectURL(new Blob([viewerHtml], { type: 'text/html' }));
            this.offlineBlobUrls.push(viewerBlobUrl);
        }

        return viewerBlobUrl;
    };

    /**
     * Builds a minimal pdf.js-powered PDF viewer as an HTML string, referencing
     * pdf.js and pdf.worker.js via blob URLs so no network requests are needed.
     */
    buildOfflinePdfViewerHtml = (pdfBlobUrl, pdfJsBlobUrl, pdfWorkerBlobUrl) => {
        const pdfUrl = JSON.stringify(pdfBlobUrl);
        const workerUrl = JSON.stringify(pdfWorkerBlobUrl);
        return `<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<title>PDF Viewer</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{background:#404040;font-family:sans-serif;display:flex;flex-direction:column;height:100vh;overflow:hidden}
#toolbar{background:#474747;padding:4px 8px;display:flex;align-items:center;gap:6px;color:#fff;min-height:32px;flex-shrink:0}
.tb{background:#6e6e6e;color:#fff;border:none;padding:3px 8px;cursor:pointer;border-radius:2px;font-size:14px}
.tb:hover{background:#888}
#pageinfo{font-size:13px;margin:0 4px}
#viewerContainer{flex:1;overflow:auto;display:flex;flex-direction:column;align-items:center;padding:10px;gap:8px}
canvas{display:block;box-shadow:0 2px 8px rgba(0,0,0,.5)}
#msg{color:#fff;align-self:center;font-size:15px;margin-top:20px}
</style>
</head><body>
<div id="toolbar">
  <button class="tb" id="bPrev">&#9664;</button>
  <button class="tb" id="bNext">&#9654;</button>
  <span id="pageinfo"></span>
  <button class="tb" id="bZoomOut">&#8722;</button>
  <button class="tb" id="bZoomIn">&#43;</button>
</div>
<div id="viewerContainer"><span id="msg">Loading PDF\u2026</span></div>
<script src="${pdfJsBlobUrl}"></script>
<script>
(function(){
  var lib=window.pdfjsLib||window['pdfjs-dist/build/pdf'];
  if(!lib){document.getElementById('msg').textContent='PDF.js not available';return;}
  lib.GlobalWorkerOptions.workerSrc=${workerUrl};
  var container=document.getElementById('viewerContainer');
  var msg=document.getElementById('msg');
  var pageinfo=document.getElementById('pageinfo');
  var pdf=null,cur=1,total=0,scale=1.5;
  var canvases=[];
  function info(){pageinfo.textContent='Page '+cur+' / '+total;}
  function renderAll(){
    container.innerHTML='';
    canvases=[];
    msg.textContent='';
    for(var i=1;i<=total;i++){renderPage(i);}
  }
  function renderPage(num){
    var canvas=document.createElement('canvas');
    canvases[num]=canvas;
    container.appendChild(canvas);
    pdf.getPage(num).then(function(page){
      var vp=page.getViewport({scale:scale});
      canvas.width=vp.width;canvas.height=vp.height;
      page.render({canvasContext:canvas.getContext('2d'),viewport:vp});
    });
  }
  lib.getDocument(${pdfUrl}).promise.then(function(doc){
    pdf=doc;total=doc.numPages;cur=1;
    msg.style.display='none';
    renderAll();info();
  }).catch(function(e){msg.textContent='Error loading PDF: '+(e&&e.message||e);});
  document.getElementById('bPrev').onclick=function(){if(cur>1){cur--;info();var el=canvases[cur];if(el)el.scrollIntoView();}};
  document.getElementById('bNext').onclick=function(){if(cur<total){cur++;info();var el=canvases[cur];if(el)el.scrollIntoView();}};
  document.getElementById('bZoomIn').onclick=function(){scale=Math.min(scale*1.25,5);renderAll();};
  document.getElementById('bZoomOut').onclick=function(){scale=Math.max(scale/1.25,.5);renderAll();};
})();
</script>
</body></html>`;
    };

    refreshPresignedUrlIfNeeded = () => {
        const { model, presignedUrlsForPDFPanel } = this.props;

        if (this.isOffline()) {
            return;
        }

        if (model.pdfSourceUrl && !presignUrlService.isValidPresignedUrl(presignedUrlsForPDFPanel)) {
            presignUrlService.loadPresignedUrl(model.logicalName, model.pdfSourceUrl);
        }
    };

    calculateInteraction = () => {
        const dragBarRightElement = this.dragBarRight;
        const PDFPanelElement = this.pdfPanel;
        this.setState({
            interactionWidth: parseInt(PDFPanelElement.parentElement.offsetWidth, 10) - parseInt(dragBarRightElement.offsetWidth, 10),
            dragBarRightOffset: parseInt(dragBarRightElement.offsetLeft, 10),
        });
    };

    handleRightBarDrag = (e, ui) => {
        const { x, y } = this.state.position;
        this.setState({
            position: { x: x + ui.deltaX, y },
        });
    };

    handleBottomBarDrag = (e, ui) => {
        const { x, y } = this.state.position;
        this.setState({
            position: { x, y: y + ui.deltaY },
        });
    };

    resizePanelHorizontally = () => {
        this.setState({
            showIFrame: false,
            resizeAxis: 'h',
        });
    };

    resizePanelVertically = () => {
        this.setState({
            showIFrame: false,
            resizeAxis: 'v',
        });
    };

    showPanel = () => {
        this.setState({
            showIFrame: true,
        });
    };

    generatePDFViewerUrl = () => {
        const { model, presignedUrlsForPDFPanel } = this.props;
        const { offlinePdfBlobUrl } = this.state;
        const pdfJsService = questionHelperService.getServices().pdfJsService;

        if (window.offlineMode) {
            // Standalone offline app (Inspera Download Manager) — viewer and PDFs are
            // pre-bundled locally; use the legacy local path.
            return pdfJsService.getOfflinePdfJsUrl(model.logicalName);
        }

        if (marketplacePropertiesService.isOfflineResilienceForPdfViewerEnabled()) {
            // Prefer blob URL whenever it is available — it reads directly from CacheStorage
            // and has no network dependency. This covers both explicit offline (navigator.onLine
            // = false) and silent physical disconnects (navigator.onLine stays true but the
            // internet is actually unreachable, e.g. WiFi connected with no internet).
            if (offlinePdfBlobUrl) {
                return offlinePdfBlobUrl;
            }

            // Blob URL not yet ready (loadOfflinePdfBlobUrl is still running async).
            // Use an interim URL so the iframe is not blank while the blob loads.
            if (this.isOffline()) {
                // SW can serve the viewer from its cache immediately.
                if (this.isSwControllingPage()) {
                    return pdfJsService.getPdfJsUrl(model.pdfSourceUrl, true);
                }
                // No SW — keep the presigned URL in the iframe so there is no blank gap.
                if (presignedUrlsForPDFPanel?.presignedUrl) {
                    return pdfJsService.getPdfJsUrl(presignedUrlsForPDFPanel.presignedUrl);
                }
                return null;
            }
        }

        // Online path — presignedUrlsForPDFPanel is guaranteed valid at this call site
        // because render only invokes generatePDFViewerUrl when isValidPresignedUrl is true.
        return pdfJsService.getPdfJsUrl(presignedUrlsForPDFPanel?.presignedUrl);
    };

    onError = () => {
        this.props.onReady(false);
    };

    onReady = () => {
        this.props.onReady(true);
    };

    render() {
        const { model, presignedUrlsForPDFPanel, stimulusPosition } = this.props;
        const { interactionWidth, dragBarRightOffset, pdfPanelHeight, position, resizeAxis, showIFrame } = this.state;
        const iFrameStyle = { visibility: showIFrame ? 'visible' : 'hidden' };
        const pdfStartWidth = stimulusPosition === 'left' ? '100%' : '45%';
        const width = model.isMainMedia ? '100%' : `calc(${pdfStartWidth} + ${parseInt(position.x.toFixed(0), 10)}px)`;
        const PDFPanelStyle = {
            width,
            height: `${pdfPanelHeight + parseInt(position.y.toFixed(0), 10)}px`,
        };
        const wrapperClassNames = `${styles.PDFIframeWrapper}${showIFrame ? ' showIFrame' : ''}`;

        // When the offline resilience MPP is enabled, use isOffline() which covers both
        // window.offlineMode (legacy) and !navigator.onLine (browser offline event).
        // When disabled, fall back to window.offlineMode only — identical to pre-feature behaviour.
        const isOffline = marketplacePropertiesService.isOfflineResilienceForPdfViewerEnabled()
            ? this.isOffline()
            : window.offlineMode;
        const hasValidPresignedUrl = presignUrlService.isValidPresignedUrl(presignedUrlsForPDFPanel);
        const shouldComputeUrl = hasValidPresignedUrl || isOffline;
        const iframeUrl = (shouldComputeUrl ? this.generatePDFViewerUrl() : null) || this.state.offlinePdfBlobUrl;

        return (
            <div className={styles.PDFPanel} style={PDFPanelStyle} ref={(pdfPanel) => { this.pdfPanel = pdfPanel; }}>
                <div className={wrapperClassNames}>
                    {
                        showIFrame
                            ? null
                            : <i className={`fa fa-arrows-${resizeAxis}`} aria-hidden="true" />
                    }
                    {
                        iframeUrl
                            ? <iframe
                                onError={this.onError}
                                onLoad={this.onReady}
                                src={iframeUrl}
                                style={iFrameStyle}
                                title="pdf"
                            />
                            : null
                    }

                </div>
                <Draggable
                    axis="none"
                    bounds={{ left: -dragBarRightOffset, right: interactionWidth - dragBarRightOffset }}
                    onDrag={this.handleRightBarDrag}
                    onStart={this.resizePanelHorizontally}
                    onStop={this.showPanel}
                >
                    <div className={styles.dragBarRight} ref={(dragBarRight) => { this.dragBarRight = dragBarRight; }}>
                        <i className="fa fa-ellipsis-v" aria-hidden="true" />
                    </div>
                </Draggable>
                <Draggable
                    position={{ x: 0, y: 0 }}
                    axis="none"
                    onDrag={this.handleBottomBarDrag}
                    onStart={this.resizePanelVertically}
                    onStop={this.showPanel}
                >
                    <div className={styles.dragBarBottom}>
                        <i className="fa fa-ellipsis-h" aria-hidden="true" />
                    </div>
                </Draggable>
            </div>
        );
    }
}

export default QuestionPDFPanelDisplay;
